import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  AddCircleOutline as AddCircleOutlineIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpOutlineIcon,
  Quiz as QuizIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Button as MuiButton,
  Paper,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  GridColumns,
  GridRenderCellParams,
  GridRowId,
} from '@mui/x-data-grid-pro'

import { Button } from '@components/buttons'
import { Spinner } from '@components/loadings'
import { DataTable } from '@components/tables'
import { ChipCell } from '@components/tables/cells'

import {
  Exam,
  ExamType,
  Instruction,
  Member,
  QuestionDto,
  Site,
  SuperAdmin,
} from '@services/api'
import { Actions } from '@state/actions'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { useSelector } from '@state/store'
import {
  batchImportExams,
  deleteExam,
  findAll as findAllExam,
  findAllType,
  findInstructions,
} from '@state/thunks/procedureThunk'
import {
  bulkUpdateQuestionExams,
  findAllQuestions,
  findSiteQuestions,
  saveOrderingInfo,
  updateQuestion,
} from '@state/thunks/questionThunk'

import { DeleteConfirmationDialog } from '@components/dialogs/DeleteConfirmationDialog'
import { useAppDispatch } from '@hooks/reduxHooks'
import { useDraggable } from '@hooks/useDraggable'
import { EditIcon, UploadIcon } from 'lucide-react'
import { QuestionForm } from '../questions/QuestionForm'
import { ProcedureForm } from './ProcedureForm'

interface QuestionItemProps {
  question: QuestionDto
  onChildrenReorder: (questionId: number, reordered: QuestionDto[]) => void
  onRemove?: (question: QuestionDto) => void
}

const QuestionItem = ({
  question,
  onChildrenReorder,
  onRemove,
}: QuestionItemProps) => {
  const [confirmAnchor, setConfirmAnchor] = useState<HTMLElement | null>(null)
  const defaultChoices = [
    { id: 'yes', text: 'Oui' },
    { id: 'no', text: 'Non' },
  ]
  const choices =
    question.choices.length > 0 ? question.choices : defaultChoices

  const handleChildrenReorder = useCallback(
    (reordered: QuestionDto['childQuestions']) => {
      if (!reordered) return
      onChildrenReorder(question.id, reordered as QuestionDto[])
    },
    [question.id, onChildrenReorder],
  )

  const {
    orderedItems: orderedChildren,
    dragIndex,
    overIndex,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  } = useDraggable(question.childQuestions ?? [], handleChildrenReorder)

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 1,
        mb: 1,
        '&:last-child': { mb: 0 },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderRadius: 1,
          '&.Mui-expanded': { borderRadius: '4px 4px 0 0' },
        }}
      >
        <Box
          component="span"
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            display: 'flex',
            alignItems: 'center',
            mr: 1,
            color: 'text.disabled',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
          {question.question}
        </Typography>
        {question.target && (
          <Chip
            label={question.target === 'PATIENT' ? 'Patient' : 'Médecin'}
            size="small"
            color={question.target === 'PATIENT' ? 'primary' : 'secondary'}
            variant="outlined"
            sx={{ fontSize: 10, height: 20, mr: 1, flexShrink: 0 }}
          />
        )}
        {onRemove && (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setConfirmAnchor(e.currentTarget)
              }}
              sx={{
                color: 'text.disabled',
                '&:hover': { color: 'error.main' },
                flexShrink: 0,
              }}
              title="Retirer la question"
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Popover
              open={Boolean(confirmAnchor)}
              anchorEl={confirmAnchor}
              onClose={() => setConfirmAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Box sx={{ p: 2, textAlign: 'center', maxWidth: 250 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1.5 }}>
                  Retirer cette question de l&apos;examen ?
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center">
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setConfirmAnchor(null)}
                    text="Annuler"
                  />
                  <Button
                    color="error"
                    textColor="white"
                    size="small"
                    onClick={() => {
                      setConfirmAnchor(null)
                      onRemove(question)
                    }}
                    text="Retirer"
                  />
                </Stack>
              </Box>
            </Popover>
          </>
        )}
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 1.5, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Choix:
        </Typography>
        <Stack spacing={0.5} mt={1} mb={orderedChildren.length > 0 ? 2 : 0}>
          {choices.map((choice) => (
            <Typography key={choice.id} variant="body2" sx={{ pl: 1.5 }}>
              • {choice.text}
            </Typography>
          ))}
        </Stack>

        {orderedChildren.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              Sous-questions:
            </Typography>
            <Stack
              spacing={0.5}
              mt={1}
              sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1 }}
            >
              {orderedChildren.map((child, index) => (
                <Box
                  key={child.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => {
                    e.preventDefault()
                    handleDragOver(index)
                  }}
                  onDragEnd={handleDragEnd}
                  sx={{
                    opacity: dragIndex === index ? 0.3 : 1,
                    borderTop: '2px solid',
                    borderColor:
                      overIndex === index ? 'primary.main' : 'transparent',
                    transition: 'opacity 0.2s, border-color 0.15s',
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'grey.100',
                      bgcolor: 'background.paper',
                      '&:hover': { bgcolor: 'grey.50' },
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <DragIndicatorIcon
                        sx={{ fontSize: 14, color: 'text.disabled' }}
                      />
                      <Typography variant="body2">{child.question}</Typography>
                    </Box>
                    {(child.choices?.length ?? 0) > 0 && (
                      <Stack spacing={0.25} mt={0.5} sx={{ pl: 2 }}>
                        {child.choices?.map((c) => (
                          <Typography
                            key={c.id}
                            variant="caption"
                            color="text.secondary"
                          >
                            • {c.text}
                          </Typography>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  )
}

interface SelectQuestionModalProps {
  open: boolean
  onClose: () => void
  exam: Exam | null
  siteQuestions: QuestionDto[]
  linkedQuestions: QuestionDto[]
  loading: boolean
}

const SelectQuestionModal = ({
  open,
  onClose,
  exam,
  siteQuestions,
  linkedQuestions,
  loading,
}: SelectQuestionModalProps) => {
  const dispatch = useAppDispatch()

  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<number[]>([])
  const [questionFormOpen, setQuestionFormOpen] = useState(false)
  const [targetFilter, setTargetFilter] = useState<
    'ALL' | 'PATIENT' | 'DOCTOR'
  >('ALL')

  const handleQuestionFormClose = useCallback(() => {
    setQuestionFormOpen(false)
    if (exam?.siteId) {
      dispatch(findSiteQuestions({ siteId: exam.siteId }))
    }
  }, [dispatch, exam?.siteId])

  const linkedIds = useMemo(
    () => new Set(linkedQuestions.map((q) => q.id)),
    [linkedQuestions],
  )

  const availableQuestions = useMemo(
    () =>
      siteQuestions
        .filter((q) => !linkedIds.has(q.id))
        .filter((q) => targetFilter === 'ALL' || q.target === targetFilter),
    [siteQuestions, linkedIds, targetFilter],
  )

  const filtered = useMemo(
    () =>
      availableQuestions.filter((q) =>
        q.question.toLowerCase().includes(search.toLowerCase()),
      ),
    [availableQuestions, search],
  )

  useEffect(() => {
    if (open) {
      setSelected([])
      setSearch('')
      setTargetFilter('ALL')
    }
  }, [open])

  const handleToggle = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    )
  }

  const handleSelectAll = () => {
    setSelected(
      selected.length === filtered.length ? [] : filtered.map((q) => q.id),
    )
  }

  const handleConfirm = async () => {
    if (!exam || selected.length === 0) return
    await dispatch(
      bulkUpdateQuestionExams({ examId: exam.id, questionIds: selected }),
    )
    onClose()
  }

  const allSelected = filtered.length > 0 && selected.length === filtered.length
  const someSelected = selected.length > 0 && selected.length < filtered.length

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2.5,
            background: 'black',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={700} color="white">
                Ajouter des questions
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.72)', mt: 0.5 }}
              >
                {exam?.label}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, mt: -0.5 }}>
              <IconButton
                size="small"
                onClick={() => setQuestionFormOpen(true)}
                sx={{ color: 'rgba(255,255,255,0.8)' }}
                title="Créer une question"
              >
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={onClose}
                sx={{ color: 'rgba(255,255,255,0.8)' }}
                title="Fermer"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5 }}>
            {(['ALL', 'PATIENT', 'DOCTOR'] as const).map((t) => (
              <Chip
                key={t}
                label={
                  t === 'ALL' ? 'Tous' : t === 'PATIENT' ? 'Patient' : 'Médecin'
                }
                size="small"
                onClick={() => setTargetFilter(t)}
                sx={{
                  cursor: 'pointer',
                  bgcolor:
                    targetFilter === t
                      ? 'rgba(255,255,255,0.3)'
                      : 'rgba(255,255,255,0.1)',
                  color: 'white',
                  fontWeight: targetFilter === t ? 700 : 400,
                  border: '1px solid',
                  borderColor:
                    targetFilter === t ? 'white' : 'rgba(255,255,255,0.3)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher une question..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.15)',
                borderRadius: 2,
                color: 'white',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                '&.Mui-focused fieldset': { borderColor: 'white' },
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255,255,255,0.55)',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {!loading && availableQuestions.length > 0 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                sx={{ p: 0 }}
              />
              <Typography variant="caption" color="text.secondary">
                Tout sélectionner ({filtered.length})
              </Typography>
            </Box>
            {selected.length > 0 && (
              <Chip
                label={`${selected.length} sélectionnée(s)`}
                size="small"
                color="primary"
                sx={{ fontWeight: 600, fontSize: 11 }}
              />
            )}
          </Box>
        )}

        <DialogContent sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Spinner />
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
              <HelpOutlineIcon
                sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {availableQuestions.length === 0
                  ? 'Toutes les questions de ce site sont déjà liées à cet examen.'
                  : 'Aucune question trouvée pour cette recherche.'}
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {filtered.map((q) => {
                const isSelected = selected.includes(q.id)
                return (
                  <Box
                    key={q.id}
                    onClick={() => handleToggle(q.id)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'primary.50' : 'background.paper',
                      transition: 'background-color 0.15s',
                      '&:hover': {
                        bgcolor: isSelected ? 'primary.100' : 'grey.50',
                      },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      size="small"
                      sx={{ p: 0, mt: 0.25 }}
                      onChange={() => handleToggle(q.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 600 : 400}
                        color={isSelected ? 'primary.main' : 'text.primary'}
                        noWrap
                      >
                        {q.question}
                      </Typography>
                      {(q.childQuestions?.length ?? 0) > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {q.childQuestions?.length} sous-question(s)
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <Chip
                        label={q.type}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 10, height: 20 }}
                      />
                      {isSelected && (
                        <CheckCircleIcon
                          sx={{ fontSize: 16, color: 'primary.main' }}
                        />
                      )}
                    </Box>
                  </Box>
                )
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'grey.200',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {linkedIds.size} déjà liée(s) · {selected.length} à ajouter
          </Typography>
          <Button variant="outlined" onClick={onClose} text="Annuler" />
          <Button
            onClick={handleConfirm}
            text="Confirmer"
            disabled={selected.length === 0}
            variant="outlined"
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={questionFormOpen}
        onClose={handleQuestionFormClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Create Question</DialogTitle>
        <DialogContent>
          <QuestionForm
            onClose={handleQuestionFormClose}
            selectedExamId={exam?.id}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

interface QuestionsPopoverProps {
  anchorEl: HTMLElement | null
  onClose: () => void
  questions: QuestionDto[]
  loading: boolean
  exam: Exam | null
  onRefresh: () => void
}

const QuestionsPopover = ({
  anchorEl,
  onClose,
  questions,
  loading,
  exam,
  onRefresh,
}: QuestionsPopoverProps) => {
  const open = Boolean(anchorEl)
  const dispatch = useAppDispatch()
  const [selectModalOpen, setSelectModalOpen] = useState(false)
  const pendingOrderRef = useRef<QuestionDto[]>(questions)

  useEffect(() => {
    pendingOrderRef.current = questions
  }, [questions])

  const { siteQuestions, siteQuestionsLoading } = useSelector(
    ({ question, loading }) => ({
      siteQuestions: question.siteQuestions ?? [],
      siteQuestionsLoading: loading[Actions.QUESTION_FIND_BY_SITE],
    }),
  )

  const handleOpenSelectModal = useCallback(() => {
    if (!exam?.siteId) return
    dispatch(findSiteQuestions({ siteId: exam.siteId }))
    setSelectModalOpen(true)
  }, [dispatch, exam?.siteId])

  const handleQuestionsReorder = useCallback((reordered: QuestionDto[]) => {
    pendingOrderRef.current = reordered
  }, [])

  const handleChildrenReorder = useCallback(
    (questionId: number, reordered: QuestionDto[]) => {
      pendingOrderRef.current = pendingOrderRef.current.map((q) =>
        q.id === questionId ? { ...q, childQuestions: reordered } : q,
      )
    },
    [],
  )

  const handleClose = useCallback(() => {
    if (exam?.id && pendingOrderRef.current.length > 0) {
      dispatch(
        saveOrderingInfo({
          items: pendingOrderRef.current
            .filter((q) => q.id !== undefined)
            .map((q, index) => ({
              examId: exam.id,
              questionId: q.id,
              sortIndex: index,
              childQuestion:
                q.childQuestions
                  ?.filter((child) => child.id !== undefined)
                  .map((child, childIndex) => ({
                    childQuestionId: child.id!,
                    orderingIndex: childIndex,
                  })) ?? [],
            })),
        }),
      )
    }
    onClose()
  }, [dispatch, exam?.id, onClose])

  const handleRemoveQuestion = useCallback(
    async (question: QuestionDto) => {
      if (!exam?.id) return
      const newExamIds = question.exams
        .map((e) => e.id)
        .filter((id) => id !== exam.id)
      await dispatch(
        updateQuestion({
          id: question.id,
          body: { examIds: newExamIds },
        }),
      )
      dispatch(findAllQuestions({ examId: exam.id }))
      onRefresh()
    },
    [dispatch, exam?.id, onRefresh],
  )

  const handleSelectModalClose = useCallback(() => {
    if (exam?.id) {
      dispatch(findAllQuestions({ examId: exam.id }))
    }
    onRefresh()
    setSelectModalOpen(false)
  }, [dispatch, exam?.id, onRefresh])

  const {
    orderedItems: orderedQuestions,
    dragIndex,
    overIndex,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  } = useDraggable(questions, handleQuestionsReorder)

  return (
    <>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              maxWidth: 800,
              minWidth: 600,
              maxHeight: 500,
              overflow: 'auto',
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" fontWeight={600}>
              Questions associées
            </Typography>
            <IconButton
              size="small"
              onClick={handleOpenSelectModal}
              color="primary"
              title="Ajouter des questions"
            >
              <AddCircleOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Spinner />
            </Box>
          ) : orderedQuestions.length > 0 ? (
            <Stack spacing={1}>
              {orderedQuestions.map((q, index) => (
                <Box
                  key={q.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => {
                    e.preventDefault()
                    handleDragOver(index)
                  }}
                  onDragEnd={handleDragEnd}
                  sx={{
                    opacity: dragIndex === index ? 0.3 : 1,
                    borderTop: '2px solid',
                    borderColor:
                      overIndex === index ? 'primary.main' : 'transparent',
                    transition: 'opacity 0.2s, border-color 0.15s',
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' },
                  }}
                >
                  <QuestionItem
                    question={q}
                    onChildrenReorder={handleChildrenReorder}
                    onRemove={handleRemoveQuestion}
                  />
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: 'center' }}
            >
              Aucune question
            </Typography>
          )}
        </Box>
      </Popover>

      <SelectQuestionModal
        open={selectModalOpen}
        onClose={handleSelectModalClose}
        exam={exam}
        siteQuestions={siteQuestions}
        linkedQuestions={questions}
        loading={siteQuestionsLoading}
      />
    </>
  )
}

interface ProcedureDialogProps {
  open: boolean
  onClose: () => void
  exam?: Exam
}

const ProcedureDialog = ({ open, onClose, exam }: ProcedureDialogProps) => {
  const intl = useIntl()

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      sx={{ 
        '& .MuiDialog-paper': { 
          marginBottom: '80px'
        } 
      }}>
      <DialogTitle>
        {exam
          ? intl.formatMessage({ id: 'editExam' })
          : intl.formatMessage({ id: 'addExam' })}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <ProcedureForm onClose={onClose} row={exam} />
      </DialogContent>
    </Dialog>
  )
}

interface SearchBarProps {
  sites: Site[]
  selectedSiteId: number
  onSiteChange: (siteId: number) => void
  onSearchChange: (value: string) => void
  user?: Member | SuperAdmin
  onImport: (file: File) => void
}

const SearchBar = ({
  sites,
  selectedSiteId,
  onSiteChange,
  onSearchChange,
  user,
  onImport,
}: SearchBarProps) => {
  const intl = useIntl()

  return (
    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
      <TextField
        select
        label="Site"
        value={selectedSiteId}
        onChange={(e) => onSiteChange(Number(e.target.value))}
        sx={{
          width: 220,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            borderRadius: 2,
          },
        }}
        size="small"
      >
        <MenuItem value={-1}>Tous les sites</MenuItem>
        {sites.map((site) => (
          <MenuItem key={site.id} value={site.id}>
            {site.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={intl.formatMessage({ id: 'search' })}
        placeholder="Nom de l'examen..."
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{
          flex: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#fff',
            borderRadius: 2,
          },
        }}
        size="small"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />
      {user?.account.role === 'super-admin' && (
        <MuiButton
          variant="contained"
          color="info"
          size="small"
          startIcon={<UploadIcon size={16} />}
          component="label"
          disabled={selectedSiteId === -1}
        >
          {intl.formatMessage({ id: 'import-csv' })}
          <input
            type="file"
            hidden
            accept=".csv"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                onImport(file)
              }
              e.target.value = ''
            }}
          />
        </MuiButton>
      )}
    </Stack>
  )
}

export default function Procedures() {
  const dispatch = useAppDispatch()
  const intl = useIntl()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [examToEdit, setExamToEdit] = useState<Exam | undefined>()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<number[]>([])
  const [questionsAnchor, setQuestionsAnchor] = useState<HTMLElement | null>(
    null,
  )
  const [currentExam, setCurrentExam] = useState<Exam | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [selectedSiteId, setSelectedSiteId] = useState(-1)

  const { loading, exams, user } = useSelector(
    ({ procedure, loading, auth }) => ({
      exams: procedure.exams.datas,
      loading:
        loading[Actions.PROCEDURE_FIND_ALL] ||
        loading[Actions.PROCEDURE_CREATE] ||
        loading[Actions.PROCEDURE_UPDATE],
      user: auth.user,
    }),
  )

  const sites = (user as unknown as { sites: Site[] })?.sites || []

  const { loading: questionLoading, questions } = useSelector(
    ({ question, loading }) => ({
      questions: question.questions,
      loading: loading[Actions.QUESTION_FIND_ALL],
    }),
  )

  const handleOpenQuestions = useCallback(
    (event: React.MouseEvent<HTMLElement>, exam: Exam) => {
      setQuestionsAnchor(event.currentTarget)
      setCurrentExam(exam)
      dispatch(findAllQuestions({ examId: exam.id }))
    },
    [dispatch],
  )

  const handleCloseQuestions = useCallback(() => {
    setQuestionsAnchor(null)
    setCurrentExam(null)
  }, [])

  const handleOpenDialog = useCallback((exam?: Exam) => {
    setExamToEdit(exam)
    setDialogOpen(true)
  }, [])

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false)
    setExamToEdit(undefined)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    dispatch(deleteExam(selectedRows))
    setDeleteDialogOpen(false)
    setSelectedRows([])
  }, [dispatch, selectedRows])

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length > 0) {
      setDeleteDialogOpen(true)
    } else {
      dispatch(
        enqueueSnackbar({
          message: intl.formatMessage({ id: 'noExamSelected' }),
          type: 'error',
        }),
      )
    }
  }, [dispatch, intl, selectedRows.length])

  const onRefresh = useCallback(() => {
    dispatch(findAllType())
    dispatch(
      findAllExam({
        search: searchValue,
        siteIds: selectedSiteId > 0 ? [selectedSiteId] : undefined,
      }),
    )
    dispatch(findInstructions())
  }, [dispatch, searchValue, selectedSiteId])

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchValue(value), 300),
    [],
  )

  const handleImport = useCallback(
    async (file: File, siteId: number) => {
      try {
        await dispatch(batchImportExams({ file, siteId })).unwrap()
        dispatch(
          enqueueSnackbar({
            message: intl.formatMessage({ id: 'importSuccess' }),
            type: 'success',
          }),
        )
        onRefresh()
      } catch (error: any) {
        dispatch(
          enqueueSnackbar({
            message:
              error?.message || intl.formatMessage({ id: 'importError' }),
            type: 'error',
          }),
        )
      }
    },
    [dispatch, intl, onRefresh],
  )

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'questions',
        headerName: '',
        width: 50,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            size="small"
            onClick={(e) => handleOpenQuestions(e, params.row as Exam)}
            sx={{ color: 'text.secondary' }}
          >
            <QuizIcon fontSize="small" />
          </IconButton>
        ),
      },
      {
        field: 'code',
        headerName: intl.formatMessage({ id: 'code' }),
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'label',
        headerName: intl.formatMessage({ id: 'label' }),
        flex: 2,
        minWidth: 200,
      },
      {
        field: 'examTypes',
        headerName: intl.formatMessage({ id: 'type' }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as ExamType[] | undefined
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {value?.slice(0, 2).map((type) => (
                <Chip
                  key={type.id}
                  label={type.label}
                  size="small"
                  variant="outlined"
                />
              ))}
              {value && value.length > 2 && (
                <Chip
                  label={`+${value.length - 2}`}
                  size="small"
                  color="primary"
                />
              )}
            </Stack>
          )
        },
      },
      {
        field: 'modality',
        headerName: intl.formatMessage({ id: 'modality' }),
        width: 100,
      },
      {
        field: 'duration',
        headerName: intl.formatMessage({ id: 'time' }),
        width: 80,
        renderCell: (params: GridRenderCellParams) =>
          params.value ? `${params.value} min` : '-',
      },
      {
        field: 'instructionsList',
        headerName: intl.formatMessage({ id: 'instructions' }),
        flex: 1,
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => {
          const value = params.value as Instruction[] | undefined
          return (
            <Typography variant="body2" color="text.secondary" noWrap>
              {value?.map((v) => v.label).join(', ') || '-'}
            </Typography>
          )
        },
      },
      {
        field: 'sex',
        headerName: intl.formatMessage({ id: 'gender' }),
        width: 80,
        renderCell: (params: GridRenderCellParams) => {
          const labels: Record<string, string> = {
            M: 'Homme',
            F: 'Femme',
            A: 'Tous',
          }
          return labels[params.value as string] || params.value
        },
      },
      {
        field: 'bodyPart',
        headerName: intl.formatMessage({ id: 'bodyPart' }),
        width: 120,
        renderCell: ChipCell,
      },
      {
        field: 'ageRange',
        headerName: intl.formatMessage({ id: 'ageRange' }),
        width: 100,
      },
      {
        field: 'contrast',
        headerName: intl.formatMessage({ id: 'contrast' }),
        width: 80,
        type: 'boolean',
      },
      {
        field: 'dose',
        headerName: intl.formatMessage({ id: 'dose' }),
        width: 80,
        type: 'boolean',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 100,
        sortable: false,
        align: 'right',
        renderCell: (params: GridRenderCellParams) => (
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row as Exam)}
          >
            <EditIcon />
          </IconButton>
        ),
      },
    ],
    [intl, handleOpenQuestions, handleOpenDialog],
  )

  return (
    <Box sx={{ height: '95%', display: 'flex', flexDirection: 'column' }}>
      <SearchBar
        sites={sites}
        selectedSiteId={selectedSiteId}
        onSiteChange={setSelectedSiteId}
        onSearchChange={debouncedSearch}
        user={user}
        onImport={(file) => handleImport(file, selectedSiteId)}
      />

      <Paper
        elevation={1}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataTable
          rows={exams}
          columns={columns}
          loading={loading}
          toolBarProps={{
            title: 'procedures',
            icon: 'fa fa-syringe',
            onAdd: () => handleOpenDialog(),
            onRefresh,
            onDelete: handleDeleteClick,
          }}
          detailPanelHeight={400}
          checkboxSelection
          onSelectionModelChange={(selection: GridRowId[]) =>
            setSelectedRows(selection as number[])
          }
        />
      </Paper>

      <ProcedureDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        exam={examToEdit}
      />

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirmer la suppression"
        description="Voulez-vous vraiment supprimer les examens sélectionnés ?"
      />

      <QuestionsPopover
        anchorEl={questionsAnchor}
        onClose={handleCloseQuestions}
        questions={questions}
        loading={questionLoading}
        exam={currentExam}
        onRefresh={onRefresh}
      />
    </Box>
  )
}
